import { useEffect, useState } from 'react';
import { ArchiveIcon, ShieldAlertIcon } from 'lucide-react';
import { app, exportApi } from '@/lib/bridge';
import { describeError } from '@/lib/errorMessage';
import { useAccounts } from '@/lib/useAccounts';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { useGroupedSurface } from '@/lib/useGroupedSurface';
import { AccountCard } from '@/components/AccountCard';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/skeletons';
import { InfoCard } from '@/components/task-ui';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { CARD, HINT, MUTED } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 薯薯全能通 · 导出笔记（`/shushu/export?accountId=`）。
 *
 * 对照搬小书「下载并导出笔记」：把本机已备份的笔记（备份时下载的原图 / 视频 / 文字）打成 zip，
 * 弹系统分享（存到「文件」、隔空投送、发给电脑）。每篇一个文件夹，有评论备份的一起带上 comments.json。
 * 不再联网下载，只打包本机已有的；没备份的先去「数据备份」。
 */

const MIN_BRIDGE = 6;

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function ShushuExportPage() {
  const { info, loading: infoLoading, isNative } = useAppInfo();
  const { accounts, selectedId: accountId, loading: accountsLoading, error: accountError } = useAccounts();
  const [summary, setSummary] = useState<{ notes: number; bytes: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useGroupedSurface();

  const account = accounts.find((a) => a.id === accountId) ?? null;
  const outdated = !!info && info.bridgeVersion < MIN_BRIDGE;
  const loaded = !infoLoading && !accountsLoading && (summary !== null || outdated || !accountId || error !== null);
  useAppReady(loaded);

  useEffect(() => {
    if (!accountId || outdated || infoLoading) return;
    let cancelled = false;
    exportApi
      .summary(accountId)
      .then((value) => {
        if (!cancelled) setSummary(value);
      })
      .catch((e) => {
        if (!cancelled) setError(describeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, outdated, infoLoading]);

  const run = async () => {
    if (!accountId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const value = await exportApi.posts(accountId);
      setResult(`已打包 ${value.notes} 篇笔记（${formatBytes(value.bytes)}）。`);
      void app.haptic('success');
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="导出笔记" description="把本机备份的笔记打包导出">
      {!loaded ? <PageSkeleton kind="export" /> : null}

      {loaded && !isNative ? (
        <InfoCard title="请在 App 内打开">浏览器里单独调试请加 <code>?mock=1</code>。</InfoCard>
      ) : null}
      {outdated ? (
        <InfoCard title="需要更新 App" icon={<ShieldAlertIcon className="text-primary size-5 shrink-0" />}>
          当前 App 版本还不支持导出笔记，请更新后再试。
        </InfoCard>
      ) : null}

      {loaded && account && summary ? (
        <>
          <AccountCard account={account} showId={false} />
          <Card className={CARD}>
            <p className="text-callout font-semibold">打包下载</p>
            <p className={MUTED}>
              {summary.notes
                ? `本机已备份 ${summary.notes} 篇笔记，约 ${formatBytes(summary.bytes)}。会打成一个 zip，每篇一个文件夹（原图 / 视频 / 文字，有评论备份的带上评论）。`
                : '本机还没有备份过的笔记，先去「数据备份」备份后再导出。'}
            </p>
            <p className={HINT}>打包完成后会弹出系统分享，可以存到「文件」、隔空投送或发给电脑。</p>
          </Card>
          {summary.notes === 0 ? (
            <Button
              variant="tinted"
              className="w-full"
              onClick={() => void app.openPage(`/shushu/backup?accountId=${encodeURIComponent(account.id)}`, '数据备份')}
            >
              去数据备份
            </Button>
          ) : null}
          <Button
            className={cn('disabled:bg-primary/35 disabled:text-primary-foreground w-full', summary.notes === 0 && 'hidden')}
            disabled={busy}
            onClick={() => void run()}
          >
            {busy ? (
              <>
                <Spinner />
                正在打包…
              </>
            ) : (
              <>
                <ArchiveIcon data-icon="inline-start" />
                打包并导出
              </>
            )}
          </Button>
          {result ? <p className={cn('mx-1', HINT)}>{result}</p> : null}
        </>
      ) : null}

      {(error ?? accountError) ? <p className="text-destructive px-1 text-footnote">{error ?? accountError}</p> : null}
    </PageShell>
  );
}
